import React from 'react';

import { useAppContext, useAppStaticContext } from './AppContext';
import { appConfig } from './config';
import { env } from './env';
import { useHref } from './router/router';
import { useViolettaAddToCart } from './useViolettaAddToCart';
import { useViolettaRequestLogin } from './useViolettaRequestLogin';
import { addScript } from './util';
import { violetta, type ViolettaConfig } from './violettaSdk';

// Istanza violetta di default. Sovrascrivibile per ambiente con
// REACT_APP_VIOLETTA_URL, o per singolo store con `violetta.url`.
const DEFAULT_VIOLETTA_URL = 'https://violetta.b2x.it';

// I due guard qui sotto sono a livello di modulo e non ref, ed è deliberato: lo
// stato che difendono vive nel bundle dell'sdk, cioè quanto la PAGINA, mentre una
// ref vive quanto l'istanza del componente. Finché l'App monta questo componente
// una volta sola le due durate coincidono, ma è una coincidenza da non firmare in
// un contratto. Stessa forma e stessa ragione del guard di useViolettaAddToCart.
let readyHandlerRegistered = false;
let initSent = false;

// Integrazione violetta a livello di pagina: script, configurazione (il comando
// "init", che è uno solo e vale per entrambe le superfici) e handler degli eventi.
// L'App lo monta una volta sola e non lo smonta mai, ed è il motivo per cui vive
// qui e non nel componente <Violetta> della pagina assistente: quello si monta e
// si smonta con la route, e con la sola modalità embed accesa non esisterebbe
// nessuno a chiamare la init — il "mountEmbed" resterebbe in coda per sempre.
//
// Non è esportato da export.ts di proposito: montarlo una seconda volta
// significherebbe una seconda init, che l'sdk scarta con un warn.
export const ViolettaRoot = () => {
  // Rinominata: `violetta` in questo file è il namespace dell'sdk importato sopra,
  // mentre questa è la configurazione del brand, che è un'altra cosa.
  const { api, violetta: violettaConfig } = appConfig;
  const { locale, routesMapByCode } = useAppContext();
  const { getPagePath } = useAppStaticContext();

  const enableEmbed = violettaConfig?.enableEmbed ?? false;
  const enableWidget = violettaConfig?.enableWidget ?? false;
  // Basta una delle due superfici: la configurazione è una sola e le serve
  // entrambe. Due gate separati (uno per modalità, come nella versione a due init)
  // lascerebbero lo store con il solo embed senza nessuno a chiamare la init.
  const enable = enableEmbed || enableWidget;

  // L'istanza violetta va scelta per ambiente: violetta risolve la storefront-api
  // con una base URL cablata nel proprio deploy, quindi l'istanza di produzione
  // accetta solo apiKey di produzione. Da un ambiente di collaudo va puntata
  // l'istanza di collaudo, altrimenti la key viene rifiutata e la chat risponde
  // "Sessione storefront-api mancante".
  const sdkSrc = `${(violettaConfig?.url ?? env.REACT_APP_VIOLETTA_URL ?? DEFAULT_VIOLETTA_URL).replace(
    /\/+$/,
    '',
  )}/sdk.js`;
  // Key dedicata a violetta se configurata, altrimenti la stessa api.apiKey già
  // usata dallo store verso lo storefront-api (già lato client, nessuna nuova
  // esposizione). Violetta la inoltrerà come header API-Key.
  const apiKey = violettaConfig?.apiKey ?? api?.apiKey;

  // Pagina che ospita l'embed, esclusa dal widget. Non è più una questione di
  // correttezza: l'sdk il widget non lo monta dove c'è un embed, e se ne accorge da
  // sé anche navigando. Serve solo a non farne comparire il trigger per un istante
  // navigando VERSO quella pagina, dove il "mountEmbed" parte dopo il render del
  // componente che ospita il container.
  //
  // Il codice pagina si legge solo quando entrambe le superfici sono accese: senza
  // widget non c'è niente da escludere, senza embed non c'è nessun lampo da
  // evitare, e in quei casi risolvere il path costerebbe solo l'attesa delle route
  // qui sotto, ritardando la init per niente.
  //
  // Il path si risolve dal codice pagina, quindi dipende dalle route, che arrivano
  // da un'api. `routesReady` distingue "route non ancora arrivate" da "codice
  // pagina inesistente", che dal solo `undefined` di `getPagePath` non si vedono.
  const routesReady = routesMapByCode !== undefined;
  const embedPageCode = enableEmbed && enableWidget ? violettaConfig?.embedPageCode : undefined;
  const embedPagePath = embedPageCode === undefined ? undefined : getPagePath(embedPageCode);
  // Lo sdk confronta con `window.location.pathname`, che comprende il basename
  // del router, mentre `getPagePath` restituisce un path relativo al basename
  // (è il valore che si passa a `to=` dei Link). `useHref` applica il basename
  // effettivo del BrowserRouter, che è l'unico modo di non duplicare qui la
  // condizione di App.tsx su quando quel basename porta il token locale-country
  // (`getRouterBasename(locale, shippingCountry)` chiamato a mano su uno store
  // mono-locale produrrebbe un prefisso che il router non ha).
  // Chiamato sempre, come vuole la regola degli hook: il valore si usa solo se
  // c'è un path da escludere.
  const embedPageHref = useHref(embedPagePath ?? '/');
  // Un solo path, senza la variante con lo slash finale: la normalizzazione è
  // dell'sdk.
  const excludedPaths = embedPagePath === undefined ? undefined : embedPageHref;

  // Codice pagina configurato ma non presente nel router: la configurazione dice
  // una cosa che il router non conferma, e va detto. La conseguenza non è più un
  // bug di correttezza (le due modalità convivono comunque), ma il lampo del
  // trigger a ogni ingresso nella pagina dell'assistente.
  React.useEffect(() => {
    if (routesReady && embedPageCode !== undefined && embedPagePath === undefined) {
      // eslint-disable-next-line no-console
      console.error(
        `appConfig.violetta.embedPageCode "${embedPageCode}" non corrisponde a nessuna route: il trigger del widget violetta comparirà per un istante a ogni ingresso nella pagina dell'assistente.`,
      );
    }
  }, [embedPageCode, embedPagePath, routesReady]);

  // Lo script è un singleton di pagina, come ogni altro script terzo caricato con
  // addScript (gtm, ga4, clarity, hotjar...): si carica una volta e resta.
  // Nessun cleanup, di proposito. L'sdk non espone un comando "destroy": togliere
  // lo script dal dom non azzererebbe lo stato del bundle già eseguito (il
  // listener dei postMessage, la patch di history per la navigazione spa), quindi
  // un re-add produrrebbe listener duplicati invece di un reset.
  React.useEffect(() => {
    if (!enable) {
      return;
    }

    addScript({
      defer: true,
      id: 'violetta-sdk',
      src: sdkSrc,
    });
  }, [enable, sdkSrc]);

  // Rete di sicurezza per l'identità: l'iframe non la conserva fra i remount
  // (l'embed che si monta entrando nella pagina dell'assistente, o il widget che
  // l'sdk rimonta uscendone), quindi a ogni "ready" gli si rimanda l'utente
  // corrente. Registrato prima della init, che per "on" è ammesso.
  // Nessuna dipendenza dai valori: l'id corrente lo tiene violettaSdk.ts.
  //
  // Il guard è a livello di modulo e non basta l'effect con le sole `[enable]`:
  // gli handler dell'sdk si accumulano e non si rimuovono, e in
  // `React.StrictMode` (attivo su tutti gli store) l'effect gira due volte in
  // sviluppo. Qui il danno sarebbe solo un secondo "login" identico, ma la regola
  // è la stessa di useViolettaAddToCart, dove invece raddoppierebbe le aggiunte
  // al carrello.
  React.useEffect(() => {
    if (!enable || readyHandlerRegistered) {
      return;
    }

    readyHandlerRegistered = true;
    violetta.command('on', 'ready', violetta.resendUser);
  }, [enable]);

  useViolettaAddToCart(enable);
  useViolettaRequestLogin(enable);

  // La configurazione è una sola per pagina, e la manda solo questo effect.
  //
  // La guardia sta DENTRO l'effect e non su un `return null` anticipato, come
  // faceva la versione a due componenti: lì il gate funzionava perché impediva il
  // montaggio del figlio che chiamava l'sdk, qui il chiamante è questo stesso
  // componente e i suoi effect girano anche quando il render non produce niente.
  React.useEffect(() => {
    // Init solo a locale noto: `locale` arriva dalla sessione, quindi è undefined
    // per i primi render. Inizializzare subito monterebbe la chat senza dichiarare
    // la lingua, e la init è idempotente (la prima vince, le successive vengono
    // scartate con un warn), quindi la lingua non si recupererebbe più. In
    // italiano non si noterebbe, perché è il default di violetta.
    //
    // E, quando c'è una pagina embed da escludere, solo a route caricate: le route
    // arrivano da una fetch propria e non sono ancora lì quando la sessione è già
    // nota. Sempre per l'idempotenza, un'esclusione mancante alla prima chiamata
    // non sarebbe più correggibile, e il lampo del trigger diventerebbe permanente
    // per tutta la sessione. Il gate è sulle route e non su `excludedPaths`, così
    // un embedPageCode sbagliato in config non blocca la init per sempre: a route
    // caricate si prosegue comunque, con l'errore già segnalato dall'effect qui
    // sopra.
    if (!enable || !locale || (embedPageCode !== undefined && !routesReady)) {
      return;
    }

    // Idempotenza lato host. Non serve alla correttezza — una seconda init l'sdk la
    // scarta da sé — ma a non spendere il suo warn: quel messaggio è scritto per
    // l'integratore che ha davvero due init, e va tenuto pulito. Serve perché
    // questo effect ha dipendenze e rigira quando arrivano (`locale` e le route in
    // momenti diversi), e perché in `React.StrictMode` gli effect girano due volte
    // in sviluppo: è la ragione per cui la console di dev mostrava sempre una
    // "init ignorata".
    if (initSent) {
      return;
    }
    initSent = true;

    const config: ViolettaConfig = {
      ...(apiKey && { apiKey }),
      locale: locale.languageCode,
      // Il widget non ha più una init sua: è una chiave di questa, e la sua
      // assenza significa "non montarlo". `true` è la scorciatoia per i default
      // dell'sdk, che sono già quelli che vogliamo (56px a 24/24, pannello
      // 400x600, chiuso) e il colore del trigger va lasciato al tema del tenant:
      // si passa un oggetto solo quando c'è davvero qualcosa da dichiarare.
      ...(enableWidget && { widget: excludedPaths === undefined ? true : { excludedPaths } }),
    };

    violetta.command('init', config);
  }, [apiKey, embedPageCode, enable, enableWidget, excludedPaths, locale, routesReady]);

  return null;
};
